import type { CaptureResult } from 'posthog-js';
import posthog from 'posthog-js';

import { getAnalyticsContext } from './context';

const TOKEN = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as
  | string
  | undefined;
const HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;

export const isAnalyticsEnabled: boolean = Boolean(TOKEN && HOST);

/**
 * Left unenriched on purpose. `$snapshot` carries session-replay chunks and is
 * emitted continuously; the heatmap events are aggregate blobs. Neither is
 * analysed per org, so nine extra scalars per payload buys nothing.
 */
const UNENRICHED_EVENTS = new Set<string>([
  '$snapshot',
  '$$heatmap',
  '$heatmaps_data'
]);

/**
 * Attach org / brand / role / page context at send time.
 *
 * posthog-js runs `before_send` synchronously inside `capture()`, after super
 * properties are merged and immediately before the event is queued. That is the
 * only hook which executes at the same instant as the SDK's automatic
 * `$pageview` — which fires ~1ms after `init()`, long before the cross-origin
 * /auth/me round trip can resolve, and again synchronously inside the patched
 * `history.pushState` on every SPA navigation. Enriching here removes those
 * ordering races instead of narrowing them, which is what the previous
 * `posthog.register()` approach could only do.
 *
 * CONTRACT: return the event to keep it. Returning `null` *or* `undefined`
 * drops it — posthog-js checks isNullish, not isUndefined — so a missing
 * `return` on any branch below silently disables all analytics. Covered by a
 * test in __tests__/posthogBeforeSend.test.ts.
 */
const enrichWithContext = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event || UNENRICHED_EVENTS.has(event.event)) return event;

  try {
    // Context wins over call-site properties: it is the authoritative snapshot,
    // and no event in events.ts passes any of these keys (call sites use the
    // from_*/to_* convention instead).
    return {
      ...event,
      properties: { ...event.properties, ...getAnalyticsContext() }
    };
  } catch {
    // Enrichment must never be the reason an event is lost.
    return event;
  }
};

/**
 * Written by the previous `register()`-based implementation. `register()`
 * persists to localStorage, so a browser that logged in under an older build
 * still carries org_name/brand_name into later anonymous visits to the public
 * landing page. Swept once at init, in the same tick, so even the automatic
 * boot `$pageview` (a 1ms timer) sees a clean slate.
 */
const LEGACY_SUPER_PROPERTIES = [
  'user_role',
  'org_id',
  'org_name',
  'org_status',
  'brand_id',
  'brand_name',
  'brand_status',
  'page_path',
  'page_name',
  'feature_key'
];

if (isAnalyticsEnabled && typeof window !== 'undefined') {
  posthog.init(TOKEN as string, {
    api_host: HOST as string,
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    before_send: enrichWithContext,
    // Boot fully opted out: no capture, and no cookies or localStorage until the
    // visitor consents. Without this, `init()` sets its cookies and fires the
    // automatic `$pageview` (~1ms later) before any React-rendered banner can
    // mount — so the only place to honour "reject" is here, at init. The
    // consent module flips these via `applyConsentToPostHog` once a stored or
    // freshly-made decision is read.
    opt_out_capturing_by_default: true,
    persistence: 'memory',
    disable_session_recording: true
  });

  LEGACY_SUPER_PROPERTIES.forEach((key) => posthog.unregister(key));
}

/**
 * Translate a consent decision into PostHog configuration.
 *
 * - analytics off        → opted out; nothing captured, nothing stored.
 * - analytics on, sale off → capture events, but memory-only persistence and no
 *                            session recording, so the visit stays anonymous and
 *                            session-scoped (AnalyticsBootstrap also skips
 *                            identify/group in this mode).
 * - analytics on, sale on  → durable persistence + recording; full analytics.
 *
 * Lives here rather than in consent.ts so the posthog-js import stays in one
 * module and consent.ts has no direct SDK dependency.
 */
export const applyConsentToPostHog = (categories: {
  analytics: boolean;
  sale: boolean;
}): void => {
  if (!isAnalyticsEnabled) return;

  if (!categories.analytics) {
    posthog.set_config({
      persistence: 'memory',
      disable_session_recording: true
    });
    posthog.opt_out_capturing();
    return;
  }

  posthog.set_config(
    categories.sale
      ? { persistence: 'localStorage+cookie', disable_session_recording: false }
      : { persistence: 'memory', disable_session_recording: true }
  );
  posthog.opt_in_capturing();
};

export { posthog };
